"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  Panel,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  CODEBOOK_COLUMNS,
  codebookRows,
  codebookToCsv,
  codebookToMarkdown,
  type IssueKind,
  parseCodebook,
  type RStyle,
  rFrameName,
  rSyntax,
  spssSyntax,
} from "@/lib/tool-kit/research-ops/codebook";

type ExportFormat = "spss" | "r" | "markdown" | "csv";

/** English strings. A translated content file overrides these through `ui`. */
export const CODEBOOK_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  inputHeading: "Your variables",
  tableLabel: "One variable per line",
  tableHint:
    "Fields in this order, separated by | or by tabs (paste straight from a spreadsheet): name | label | type | value labels | missing codes | notes. Only the name is required.",
  tablePlaceholder:
    "gender | Gender | nominal | 1=Female; 2=Male | -99\nage | Age in years | scale | | -99",
  formatNote:
    "Type is scale, ordinal, nominal or string. Value labels are code=label pairs separated by semicolons. Missing codes are separated by commas; write a range as 997 to 999, and use LO or HI for an open end.",
  rStyleLegend: "R syntax style",
  rStyleBase: "Base R (factors and attributes)",
  rStyleHaven: "haven (labelled_spss)",
  rStyleNote:
    "Base R turns value labels into factors and user-missing codes into NA. The haven style keeps the codes and labels together, as SPSS does.",
  frameLabel: "Name of the R data frame",
  framePlaceholder: "df",
  frameHint: "Used in the R syntax. An invalid name falls back to df.",
  resultsHeading: "Codebook",
  summary: "one: {count} variable read|other: {count} variables read",
  prompt: "Type or paste your variables above, or press Try an example.",
  columns: {
    name: "Name",
    label: "Label",
    type: "Type",
    values: "Value labels",
    missing: "Missing codes",
    notes: "Notes",
  },
  types: {
    scale: "Scale",
    ordinal: "Ordinal",
    nominal: "Nominal",
    string: "String",
  },
  issuesHeading: "Things to check",
  issues: {
    "no-name": "Line {line} has no variable name and was skipped.",
    type: "Line {line}: “{detail}” is not a type I know (scale, ordinal, nominal or string). It was read as scale, or as nominal when value labels are given.",
    "value-pair":
      "Line {line}: “{detail}” is not a code=label pair, so it was skipped.",
    "code-not-number":
      "Line {line}: the value code “{detail}” is not a number. Only a string variable can have text codes.",
    "duplicate-code":
      "Line {line}: the code {detail} is labelled twice. The first label was kept.",
    "missing-token":
      "Line {line}: “{detail}” is not a usable missing code or range, so it was skipped.",
    "missing-string-range":
      "Line {line}: a string variable cannot have a missing-value range (“{detail}”).",
    "name-duplicate": "The name {detail} is used more than once (line {line}).",
    "spss-name":
      "{detail} (line {line}) is not a valid SPSS name: use up to 64 bytes, start with a letter or @, do not end in a period, and avoid ALL, AND, BY, EQ, GE, GT, LE, LT, NE, NOT, OR, TO, WITH.",
    "spss-missing-limit":
      "{detail} (line {line}) has too many missing values. SPSS allows three discrete values, or one range plus one discrete value.",
    "spss-label-length":
      "The label for {detail} (line {line}) is longer than 256 bytes.",
    "spss-value-label-length":
      "A value label for {detail} (line {line}) is longer than 120 bytes.",
    "spss-line-length":
      "A label for {detail} (line {line}) would make an SPSS syntax line longer than 251 bytes. Shorten it.",
    "r-name":
      "{detail} (line {line}) is not a plain R name, so the R syntax reaches it with double square brackets.",
  },
  exportHeading: "Export",
  formatLegend: "Format",
  formats: {
    spss: "SPSS syntax",
    r: "R syntax",
    markdown: "Markdown table",
    csv: "CSV",
  },
  formatNotes: {
    spss: "Run it with your data file open: VARIABLE LABELS, VALUE LABELS, MISSING VALUES and VARIABLE LEVEL. It does not create variables or set string widths.",
    r: "Run it after reading your data into the data frame. Check each as.numeric() against your raw file before you rely on it.",
    markdown: "A table for a README or a data-management plan.",
    csv: "Opens in Excel, Sheets or R. Commas and quotes inside a cell are quoted.",
  },
  copy: "Copy",
  copied: "Copied!",
  download: "Download",
  fileNames: {
    spss: "codebook.sps",
    r: "codebook.R",
    markdown: "codebook.md",
    csv: "codebook.csv",
  },
  example:
    "name | label | type | value labels | missing codes | notes\nid | Participant ID | string\nage | Age in years | scale | | -99 | Self-report\ngender | Gender | nominal | 1=Female; 2=Male; 3=Non-binary; 4=Another identity | -99\nsat1 | I am satisfied with my course | ordinal | 1=Strongly disagree; 2=Disagree; 3=Neutral; 4=Agree; 5=Strongly agree | -99, -98\nincome | Annual household income (USD) | scale | | 997 to 999",
} as const;

const FORMATS: readonly ExportFormat[] = ["spss", "r", "markdown", "csv"];
const MIME: Record<ExportFormat, string> = {
  spss: "text/plain;charset=utf-8",
  r: "text/plain;charset=utf-8",
  markdown: "text/markdown;charset=utf-8",
  csv: "text/csv;charset=utf-8",
};

export default function CodebookGeneratorWidget() {
  const strings = useToolUi(CODEBOOK_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [format, setFormat] = useState<ExportFormat>("spss");
  const [rStyle, setRStyle] = useState<RStyle>("base");
  const [frame, setFrame] = useState("");

  const { columns, types } = strings;
  const parsed = useMemo(
    () =>
      parseCodebook(text, { headerWords: [columns.name], typeWords: types }),
    [text, columns.name, types]
  );
  const rows = useMemo(
    () =>
      codebookRows(
        parsed.variables,
        CODEBOOK_COLUMNS.map((column) => strings.columns[column]),
        strings.types
      ),
    [parsed.variables, strings]
  );
  const output = useMemo(() => {
    if (parsed.variables.length === 0) {
      return "";
    }
    switch (format) {
      case "spss":
        return spssSyntax(parsed.variables);
      case "r":
        return rSyntax(parsed.variables, rStyle, rFrameName(frame));
      case "markdown":
        return codebookToMarkdown(rows);
      default:
        return codebookToCsv(rows);
    }
  }, [format, frame, parsed.variables, rStyle, rows]);

  const issueKeys = useMemo(() => {
    const seen = new Map<string, number>();
    return parsed.issues.map((issue) => {
      const base = `${issue.kind}-${issue.line}-${issue.detail}`;
      const count = (seen.get(base) ?? 0) + 1;
      seen.set(base, count);
      return `${base}-${count}`;
    });
  }, [parsed.issues]);

  const issueText = (kind: IssueKind, line: number, detail: string) =>
    t(strings.issues[kind], {
      line: fmt(line),
      detail,
    });

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.example)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.inputHeading}>
        <TextAreaField
          hint={strings.tableHint}
          id="cb-table"
          label={strings.tableLabel}
          onChange={setText}
          placeholder={strings.tablePlaceholder}
          rows={10}
          value={text}
        />
        <p className={HINT}>{strings.formatNote}</p>
        <PillGroup
          label={strings.rStyleLegend}
          name="cb-rstyle"
          onChange={setRStyle}
          options={[
            { value: "base", label: strings.rStyleBase },
            { value: "haven", label: strings.rStyleHaven },
          ]}
          value={rStyle}
        />
        <p className={HINT}>{strings.rStyleNote}</p>
        <TextField
          hint={strings.frameHint}
          id="cb-frame"
          label={strings.frameLabel}
          mono
          onChange={setFrame}
          placeholder={strings.framePlaceholder}
          value={frame}
        />
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {parsed.variables.length === 0 ? (
          <Notice>{strings.prompt}</Notice>
        ) : (
          <>
            <p className="text-foreground text-sm">
              {tPlural(strings.summary, locale, parsed.variables.length, {
                count: fmt(parsed.variables.length),
              })}
            </p>
            <DataTable head={rows[0]} rows={rows.slice(1)} />
          </>
        )}
        {parsed.issues.length > 0 ? (
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.issuesHeading}</h3>
            <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
              {parsed.issues.map((issue, index) => (
                <li key={issueKeys[index]}>
                  {issueText(issue.kind, issue.line, issue.detail)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      <Panel title={strings.exportHeading}>
        <PillGroup
          label={strings.formatLegend}
          name="cb-format"
          onChange={setFormat}
          options={FORMATS.map((value) => ({
            value,
            label: strings.formats[value],
          }))}
          value={format}
        />
        <p className={HINT}>{strings.formatNotes[format]}</p>
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            disabled={output === ""}
            label={strings.copy}
            text={output}
          />
          <button
            className={BUTTON_SECONDARY}
            disabled={output === ""}
            onClick={() =>
              downloadText(strings.fileNames[format], output, MIME[format])
            }
            type="button"
          >
            {strings.download}
          </button>
        </div>
        {output === "" ? null : (
          <pre className="max-h-96 overflow-auto whitespace-pre rounded-md bg-muted/40 p-3 font-mono text-foreground text-xs">
            {output}
          </pre>
        )}
      </Panel>
    </div>
  );
}
